import React, { createContext, useState, useEffect, useContext } from 'react';
var GitHubColors = require('github-colors');

// Create context
const GithubContext = createContext();

// Provider component
export const GithubProvider = ({ username, children }) => {
    const [user, setUser] = useState(null);
    const [repos, setRepos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!username) {return;}

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                // Run requests in parallel
                const [userRes, repoRes] = await Promise.all([
                    fetch(`https://api.github.com/users/${username}`),
                    fetch(`https://api.github.com/users/${username}/repos`)
                ]);

                if (!userRes.ok) {throw new Error('User not found');}
                if (!repoRes.ok) {throw new Error('Failed to fetch repos');}

                const [userData, repoData] = await Promise.all([
                    userRes.json(),
                    repoRes.json()
                ]);
        
                setUser({
                    username: userData.login,
                    avatarUrl: userData.avatar_url,
                    profileUrl: userData.html_url,
                    company: userData.company,
                    location: userData.location,
                    email: userData.email
                });

                setRepos(repoData.map((repo) => {
                    const formatted = new Date(repo.updated_at).toLocaleDateString('en-US', {
                        month: 'short',   // "Nov"
                        day: 'numeric',   // 19
                        year: 'numeric'   // 2024
                    });

                    return {
                        name: repo.name,
                        link: repo.html_url,
                        description: repo.description ?? null,
                        updated: formatted,
                        language: repo.language,
                        color: GitHubColors.get(repo.language)?.color
                    };
                }));
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [username]);

    return (
        <GithubContext.Provider value={{ user, repos, loading, error }}>
            {children}
        </GithubContext.Provider>
    );
};

// Hook to use the context
export const useGithub = () => {
    return useContext(GithubContext);
};
